import { LandingPageSections } from '../LandingPage.types'
import meetUs from './meetUs'
import organizationSchema from './schemas/organizationSchema'
import webpageSchema from './schemas/productReviewWebpageSchema'

const productReviewSections: LandingPageSections[] = [
  {
    type: 'hero',
    id: '1',
    desktopWidth: 'full',
    orientation: 'overlay',
    headerText: 'Babylist Product Reviews Policy, Guidelines & Team',
    images: {
      desktop:
        'https://images.babylist.com/image/upload/v1692129787/Dynamic%20Landing%20Pages/product-review-policy-hero-D.png',
      mobile:
        'https://images.babylist.com/image/upload/v1692129787/Dynamic%20Landing%20Pages/product-review-policy-hero-M.png',
    },
  },
  {
    type: 'text',
    id: '100',
    desktopWidth: 'full',
    heading: 'How We Test and Choose Products',
    body: `<p>You may think testing baby gear and other pregnancy and parenting products sounds like a pretty great job—and you're not wrong! But choosing the <a href="/hello-baby/gear">products we feature</a> isn't all fun and games. A lot of work, thought and strategy goes into selecting and testing the products we recommend at Babylist every day.</p><p>The first step in our testing process is product discovery. Our editors and writers are fully immersed in the world of baby gear and parenting products. To discover new products, we do hours of research, build and foster relationships with brands and publicity contacts, follow countless baby and parenting social media sites and influencers and collaborate with Babylist's own internal merchandising team of buyers to stay on top of the latest and greatest in the baby and kid space.</p>
    <p>When writing about the products we recommend, our editorial process centers around a few key tenets: editorial independence, product safety and firsthand testing.</p>
<ul>
<li>
<strong>Babylist editors and writers have editorial independence.</strong> What does that mean for you as a reader and a consumer? It means we make all decisions without external influence. We are unbiased in our product recommendations and retailer and price point agnostic. And we don't show favoritism toward any brand—no matter how many free car seats they send us.</li>
<li><strong>We put safety first.</strong>
Safety is our number one priority when sharing our product recommendations. We rely on guidelines from the
<a href="https://www.aap.org/">American Academy of Pediatrics</a>
&nbsp;(AAP) and the
<a href="https://www.cdc.gov/">Centers for Disease Control and Prevention</a>
&nbsp;(CDC). We also regularly cite experts and include guidelines, recommendations and safety recall alerts from the
<a href="https://www.cpsc.gov/">U.S. Consumer Product Safety Commission</a>
&nbsp;(CPSC), an independent federal agency and global leader in consumer product safety, and from
<a href="https://ucsg.safekids.org/">Child Passenger Safety</a>
&nbsp;(CPS) technicians. If our product guides include any health information, it&rsquo;s vetted by accredited experts in medicine and/or includes citations from experts. We also collaborate with
<a href="https://www.babylist.com/health-advisory-board">Babylist&rsquo;s Health Advisory Board</a>
, a group of doctors, researchers and other health professionals from the fields of obstetrics and gynecology, psychology, pediatrics, nutrition and more.</li>
<li><strong>Testing, testing.</strong> If we don't like something, we don't think you will, either. That's why we personally test hundreds of products to find the best—and the most developmentally appropriate—products for your family. We've spent hundreds of hours dragging stroller wagons through sand, reading car seat manuals cover to cover and trying out more brands of diapers than we can count. We also draw from years of experience both as parents and as writers and researchers from our backgrounds in the fields of media, early childhood education and publishing. If we haven't tested a product ourselves, we turn to the next best source of information: you. We analyze customer reviews from hundreds of Babylist parents and from our annual survey, <a href="https://www.babylist.com/hello-baby/best-baby-products">Babylist's Best Baby Products</a> survey, which polls thousands of real Babylist families across the country and asks them to share the baby products they love the most and why.</li>
</ul>
<p>And before we publish any product guide, we ask ourselves a lot of questions. Does the curation reflect a range of price points and a diverse mix of brands? Are there safety concerns with any of the products we're recommending? Do the products and brands mesh with our corporate goals and standards around Diversity, Equity and Inclusion?</p>
<p> Our process doesn't end the day our guides go live. We recognize the importance of up-to-date information, especially when it involves products for babies and children. Our editorial team is continuously auditing and updating <a href='/hello-baby'>Babylist content</a> to make sure it reflects the latest medical and safety guidance, product information, trends and pricing.</p>`,
    background: 'none',
    includeVerticalSectionSpacing: true,
    sectionAlignment: 'left',
  },
  {
    type: 'text',
    id: '2',
    desktopWidth: 'full',
    heading: 'How We Find Deals',
    body: "We know you love a good deal, but also recognize that your life is probably pretty busy. That's why we're committed to bringing you the best deals and discounts on the products you love. (And the ones we think you'll love.) We let you know when your favorite brands are having sales and send out Sales and Deals alerts with custom product curations on Black Friday, Cyber Monday, Prime Day, Memorial Day and throughout the holiday season so you never miss a chance to score a bargain.",
    background: 'none',
    includeVerticalSectionSpacing: true,
    sectionAlignment: 'left',
  },
  {
    type: 'text',
    id: '3',
    desktopWidth: 'full',
    heading: 'How We Make Money',
    body: `<p>Babylist earns compensation from affiliate links in our content or by selling products in the Babylist Shop; however, this does not influence which products we choose to cover. Our primary goal is to share the products we've tried and loved, and we only recommended trusted retailers.\n\nWe also feature sponsored content on the site. Our free site, apps and email newsletters are made possible by our sponsors. We limit our sponsored content to relevant partners that offer products and services we believe in and use ourselves, and you'll always see \"sponsored\" somewhere within the article or guide so you can distinguish it from Babylist's organic content.</p>
    `,
    background: 'none',
    includeVerticalSectionSpacing: true,
    sectionAlignment: 'left',
  },
  {
    type: 'text',
    id: '4',
    desktopWidth: 'full',
    heading: 'How We Handle Safety and Product Recalls',
    body: 'Safety standards are constantly evolving, especially within the baby and parenting product space. We continually monitor changing standards and product recalls from the <a href="https://www.cpsc.gov/">Consumer Product Safety Commission</a> and update our content accordingly. We urge you to do the same and to register any purchases with the manufacturer so you will be alerted of any recalls.',
    background: 'none',
    includeVerticalSectionSpacing: true,
    sectionAlignment: 'left',
  },
  {
    type: 'text',
    id: '5',
    desktopWidth: 'full',
    heading: 'How to Pitch Products to Babylist',
    body: `Want to share the product you just can't live without with our editorial team? Are you a brand launching something new and exciting in the baby and parenting space? Have a question or comment, or want feedback on one of our product reviews? You can email us at <a href="mailto:content@babylist.com>content@babylist.com</a>. (Note: there's no guarantee of editorial inclusion because you send us a product.)`,
    background: 'none',
    includeVerticalSectionSpacing: true,
    sectionAlignment: 'left',
  },
  {
    type: 'text',
    id: '6',
    desktopWidth: 'full',
    heading: 'Contact Us',
    body: 'If you have questions or comments, please email us at <a href="mailto:support@babylist.com">support@babylist.com</a> or contact us via the <a href="https://help.babylist.com/hc/en-us">Babylist Help Center.</a>',
    background: 'none',
    includeVerticalSectionSpacing: true,
    sectionAlignment: 'left',
  },
  {
    type: 'frequently_asked_questions',
    id: '70',
    accordion: false,
    questions: [
      {
        question: `Does Babylist receive affiliate commissions on the products we recommend?`,
        answer: `
          Yes, Babylist may earn compensation from affiliate links in our content or by selling products in the Babylist Shop, but this does not influence which products we choose to cover. Our primary goal is to share the products we've tried and loved, and we only recommended trusted retailers.
        `,
        id: '1',
      },
      {
        question: `Does Babylist have any brand-sponsored content? How can you distinguish a branded article from the rest of Babylist's content?`,
        answer: `
          Yes, Babylist features sponsored content on the site. Our free site, apps and email newsletters are made possible by our sponsors. We limit our sponsored content to relevant partners that offer products and services we believe in and use ourselves, and you'll always see "sponsored" somewhere within the article or guide so you can distinguish it from Babylist's organic content.
        `,
        id: '2',
      },
      {
        question: `What types of backgrounds does the Babylist editorial team have? What makes them experts?`,
        answer: `
          Many of our Babylist editors and writers are parents themselves and have years of experience writing and researching, coming from media outlets like Vice, Motherly, the SF Chronicle, the New York Times and the fields of early childhood education and publishing.
        `,
        id: '3',
      },
      {
        question: `What standards and ethics guide Babylist's editorial processes?`,
        answer: `
          Our editorial process at Babylist centers around three main tenants: editorial independence, medically sound information and firsthand experience. You can read more about our standards and ethics in the <a href="/lp/editorial-policies-guidelines">Editorial Policies</a> section of our site.
        `,
        id: '4',
      },
      {
        question: `If we send a product for Babylist to review, does that mean you're going to feature it in one of your guides?`,
        answer: `
          We receive lots of products from all different brands, and we're always appreciative for the chance to learn about and try something new. But sending Babylist a product for review doesn't guarantee editorial inclusion.
        `,
        id: '5',
      },
    ],
  },
  {
    type: 'text',
    id: '1',
    desktopWidth: 'full',
    heading: 'Best Car Seats',
    background: 'none',
    includeVerticalSectionSpacing: true,
  },
  {
    type: 'guides',
    id: '2',
    heading: 'Best Car Seats',
    slugs: [
      'best-car-seats',
      'best-all-in-one-car-seats',
      'best-convertible-car-seats',
      'best-rotating-car-seats',
    ],
  },
  {
    type: 'text',
    id: '7',
    desktopWidth: 'full',
    heading: 'Best Strollers',
    background: 'none',
    includeVerticalSectionSpacing: true,
  },
  {
    type: 'guides',
    id: '8',
    heading: 'Best Strollers',
    slugs: [
      'best-strollers',
      'best-travel-systems',
      'best-budget-strollers',
      'best-double-strollers',
    ],
  },
  {
    type: 'text',
    id: '13',
    desktopWidth: 'full',
    heading: 'Best Feeding',
    background: 'none',
    includeVerticalSectionSpacing: true,
  },
  {
    type: 'guides',
    id: '14',
    heading: 'Best Feeding',
    slugs: [
      'best-high-chairs',
      'best-breast-pump',
      'best-baby-bottles',
      'best-baby-formula',
    ],
  },
  {
    type: 'text',
    id: '19',
    desktopWidth: 'full',
    heading: 'Best Nursery',
    background: 'none',
    includeVerticalSectionSpacing: true,
  },
  {
    type: 'guides',
    id: '20',
    heading: 'Best Nursery',
    slugs: [
      'best-baby-monitor',
      'best-sound-machines',
      'best-gliders',
      'best-nursery-storage',
    ],
  },
  {
    type: 'text',
    id: '25',
    desktopWidth: 'full',
    heading: 'Best Health',
    background: 'none',
    includeVerticalSectionSpacing: true,
  },
  {
    type: 'guides',
    id: '26',
    heading: 'Best Health',
    slugs: [
      'best-prenatal-vitamins',
      'best-baby-thermometers',
      'best-humidifiers',
      'best-pregnancy-tests',
    ],
  },
  {
    type: 'text',
    id: '31',
    desktopWidth: 'full',
    heading: 'Best Postpartum',
    background: 'none',
    includeVerticalSectionSpacing: true,
  },
  {
    type: 'guides',
    id: '32',
    heading: 'Best Postpartum',
    slugs: [
      'postpartum-underwear',
      'best-postpartum-recovery-products',
      'best-postpartum-recovery-tips',
      'best-postpartum-products',
    ],
  },

  {
    type: 'spacer',
    id: 'spacer-1',
  },

  ...meetUs,

  {
    type: 'schema',
    id: '22',
    schema: organizationSchema,
  },

  {
    type: 'schema',
    id: '222',
    schema: webpageSchema,
  },
]

export default productReviewSections
